import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import { Icons } from "../components/Icons";
import {
  getTrends,
  getCurrentAcademicYear,
  getAvatarUrl,
  getDashboardStats,
  getAllUsers,
} from "../services/api";
import DocumentAnalytics from "../components/DocumentAnalytics";
import { DashboardSkeleton } from "../components/SkeletonLoader";
import { useWebSocketEvent } from "../hooks/useWebSocket";

// --- Dashboard Charts Components ---

// Now takes real trend data as a prop instead of generating mock data
const TrendsChart = ({ trendData }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  // Builds chart-ready arrays from the real org-status trend data
  const fullData = useMemo(() => {
    if (!trendData || trendData.length === 0) {
      return { labels: [], pending: [], accredited: [], forInterview: [] };
    }
    return {
      labels: trendData.map((d) => {
        const date = new Date(d.date);
        return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`;
      }),
      pending: trendData.map((d) => d.pending || 0),
      accredited: trendData.map((d) => d.accredited || 0),
      forInterview: trendData.map((d) => d.for_interview || 0),
    };
  }, [trendData]);

  const windowSize = 10;
  const [startIndex, setStartIndex] = useState(
    Math.max(0, fullData.labels.length - windowSize),
  );

  // Keeps the window pinned to the latest data whenever new trend data arrives
  useEffect(() => {
    setStartIndex(Math.max(0, fullData.labels.length - windowSize));
  }, [fullData.labels.length]);

  const handlePrev = () =>
    setStartIndex((prev) => Math.max(0, prev - windowSize));
  const handleNext = () =>
    setStartIndex((prev) =>
      Math.min(
        Math.max(0, fullData.labels.length - windowSize),
        prev + windowSize,
      ),
    );

  useEffect(() => {
    if (canvasRef.current && fullData.labels.length > 0) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = canvasRef.current.getContext("2d");

      const currentLabels = fullData.labels.slice(
        startIndex,
        startIndex + windowSize,
      );
      const currentPending = fullData.pending.slice(
        startIndex,
        startIndex + windowSize,
      );
      const currentAccredited = fullData.accredited.slice(
        startIndex,
        startIndex + windowSize,
      );
      const currentForInterview = fullData.forInterview.slice(
        startIndex,
        startIndex + windowSize,
      );

      chartInstance.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: currentLabels,
          datasets: [
            {
              label: "Pending",
              data: currentPending,
              borderColor: "#f59e0b",
              backgroundColor: "rgba(245, 158, 11, 0.1)",
              tension: 0.4,
              fill: true,
              borderWidth: 2,
              pointRadius: 3,
            },
            {
              label: "Accredited/Re-accredited",
              data: currentAccredited,
              borderColor: "#10b981",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              tension: 0.4,
              fill: true,
              borderWidth: 2,
              pointRadius: 3,
            },
            {
              label: "For Interview",
              data: currentForInterview,
              borderColor: "#3b82f6",
              backgroundColor: "transparent",
              tension: 0.4,
              fill: false,
              borderWidth: 2,
              pointRadius: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 400 },
          plugins: {
            legend: { display: false },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                precision: 0,
              },
              grid: { color: "#f3f4f6" },
              border: { display: false },
            },
            x: {
              grid: { display: false },
              border: { display: false },
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
        },
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [startIndex, fullData]);

  if (fullData.labels.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        No submission activity recorded yet. The trend will build up as
        documents are submitted and reviewed.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-end mb-2 space-x-2">
        <button
          onClick={handlePrev}
          disabled={startIndex === 0}
          className={`px-3 py-1 text-[11px] uppercase tracking-wider font-bold rounded border transition-colors ${startIndex === 0 ? "text-gray-300 border-gray-100 bg-gray-50 cursor-not-allowed" : "text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"}`}
        >
          &larr; Prev
        </button>
        <button
          onClick={handleNext}
          disabled={startIndex >= fullData.labels.length - windowSize}
          className={`px-3 py-1 text-[11px] uppercase tracking-wider font-bold rounded border transition-colors ${startIndex >= fullData.labels.length - windowSize ? "text-gray-300 border-gray-100 bg-gray-50 cursor-not-allowed" : "text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"}`}
        >
          Next &rarr;
        </button>
      </div>
      <div className="flex-1 relative min-h-[200px]">
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

// Now takes real org-status counts as props
const DistributionChart = ({ pending, accredited, forInterview }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (canvasRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = canvasRef.current.getContext("2d");
      chartInstance.current = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Pending", "Accredited/Re-accredited", "For Interview"],
          datasets: [
            {
              data: [pending, accredited, forInterview],
              backgroundColor: ["#f59e0b", "#10b981", "#3b82f6"],
              borderWidth: 0,
              hoverOffset: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "70%",
          plugins: {
            legend: {
              display: false,
            },
          },
        },
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [pending, accredited, forInterview]);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} />
      <div className="absolute inset-0 flex items-center justify-center flex-col pointer-events-none text-gray-400">
        <svg
          className="w-8 h-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
          />
        </svg>
      </div>
    </div>
  );
};

// --- Dashboard Page ---
const Dashboard = () => {
  const navigate = useNavigate();

  const [greeting] = useState(() => {
    const greetings = ["Good day", "Welcome back", "Hello", "Greetings", "Hi there"];
    return greetings[Math.floor(Math.random() * greetings.length)];
  });

  // Real data state
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalActive: 0,
    totalUsers: 0,
    totalOffcampusUsers: 0,
    pending: 0,
    pendingAccreditation: 0,
    pendingOffcampus: 0,
    approved: 0,
    approvedAccreditation: 0,
    approvedOffcampus: 0,
    underMonitoring: 0,
    rejected: 0,
    rejectedForms: 0,
    rejectedOffcampus: 0,
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [recentOffcampusSubmissions, setRecentOffcampusSubmissions] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [academicYear, setAcademicYear] = useState(null);
  const [adminName, setAdminName] = useState("");

  const [avatarPath, setAvatarPath] = useState(null);
  const [userId, setUserId] = useState(null);
  const [avatarVersion] = useState(Date.now());

  // Determine admin type from localStorage
  const adminType = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.admin_type || null;
    } catch {
      return null;
    }
  })();
  const isOffCampus = adminType === "offcampus";

  // Pulls the logged-in admin's name from localStorage
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.full_name) {
        setAdminName(user.full_name.split(" ")[0]);
      }
      if (user && user.profile_picture_path) setAvatarPath(user.profile_picture_path);
      if (user && user.user_id) setUserId(user.user_id);
    } catch (e) {
      setAdminName("Admin");
    }
  }, []);

  // Calculates the countdown based on the real academic year lock date.
  // IMPORTANT: The lockDate stored in DB is a naive datetime (no timezone).
  // new Date("YYYY-MM-DDTHH:MM:SS") treats it as UTC, causing an 8-hour shift.
  // We parse manually and use Date(y,m,d,h,min,s) which always uses local time.
  const calculateTimeLeft = (lockDate) => {
    if (!lockDate) return ["00", "00", "00", "00"];
    // Strip timezone suffix if any, then split into date/time parts
    const clean = lockDate.replace(/Z|([+-]\d{2}:\d{2})$/, '');
    const [datePart, timePart = "00:00:00"] = clean.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi, s = 0] = timePart.split(':').map(Number);
    // Date(y, mo-1, d, h, mi, s) always creates a LOCAL-time Date
    const lockMs = new Date(y, mo - 1, d, h, mi, s).getTime();
    const difference = lockMs - Date.now();
    if (difference > 0) {
      return [
        Math.floor(difference / (1000 * 60 * 60 * 24))
          .toString()
          .padStart(2, "0"),
        Math.floor((difference / (1000 * 60 * 60)) % 24)
          .toString()
          .padStart(2, "0"),
        Math.floor((difference / 1000 / 60) % 60)
          .toString()
          .padStart(2, "0"),
        Math.floor((difference / 1000) % 60)
          .toString()
          .padStart(2, "0"),
      ];
    }
    return ["00", "00", "00", "00"];
  };

  const [timeLeft, setTimeLeft] = useState(["00", "00", "00", "00"]);

  // Ticks the countdown every second once we know the real lock date
  useEffect(() => {
    if (!academicYear || isOffCampus) return;
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(academicYear.lock_date));
    }, 1000);
    return () => clearInterval(timer);
  }, [academicYear, isOffCampus]);

  // Fetches all dashboard data from the backend
  const fetchDashboardData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);

      const [statsRes, trendsRes, yearRes, usersRes] = await Promise.allSettled([
        getDashboardStats(),
        getTrends(30),
        getCurrentAcademicYear(),
        getAllUsers(),
      ]);

      // Sets the academic year if one exists; otherwise leaves it null (no countdown shown)
      if (yearRes.status === "fulfilled") {
        setAcademicYear(yearRes.value.data);
        setTimeLeft(calculateTimeLeft(yearRes.value.data.lock_date));
      } else {
        setAcademicYear(null);
      }

      // Sets the trend chart data
      if (trendsRes.status === "fulfilled") {
        setTrendData(trendsRes.value.data.trends || []);
      }

      let totalUsersCount = 0;
      if (usersRes.status === "fulfilled" && Array.isArray(usersRes.value.data)) {
        totalUsersCount = usersRes.value.data.filter((u) => u.role === "end_user").length;
      }

      if (statsRes.status === "fulfilled") {
        const data = statsRes.value.data;
        setStats({
          totalActive: data.totalActive !== undefined ? data.totalActive : 0,
          totalUsers: data.totalUsers !== undefined ? data.totalUsers : totalUsersCount,
          totalOffcampusUsers: data.totalOffcampusUsers || 0,
          pending: isOffCampus ? (data.offcampusPending || 0) : (data.pending !== undefined ? data.pending : 0),
          pendingAccreditation: data.pendingAccreditation || 0,
          pendingOffcampus: data.pendingOffcampus || 0,
          approved: data.accredited || 0,
          approvedAccreditation: data.accredited || 0,
          approvedOffcampus: data.offcampusApproved || 0,
          underMonitoring: data.underMonitoring || 0,
          rejected: data.rejectedOffcampus || data.rejectedForms || 0,
          rejectedForms: data.rejectedForms || 0,
          rejectedOffcampus: data.offcampusRejected || 0,
        });
        setRecentSubmissions(data.recentSubmissions || []);
        setRecentOffcampusSubmissions(data.recentOffcampusSubmissions || []);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", () => {
    fetchDashboardData(true);
  });
  useWebSocketEvent("ORGANIZATION_STATUS_UPDATED", () => {
    fetchDashboardData(true);
  });
  useWebSocketEvent("ACCESS_REQUEST_UPDATED", () => {
    fetchDashboardData(true);
  });

  // Computes percentages for the distribution chart legend (org-level)
  const totalOrgs = stats.pending + stats.approved + stats.underMonitoring;
  const pendingPct =
    totalOrgs > 0 ? Math.round((stats.pending / totalOrgs) * 100) : 0;
  const approvedPct =
    totalOrgs > 0 ? Math.round((stats.approved / totalOrgs) * 100) : 0;
  const forInterviewPct =
    totalOrgs > 0 ? Math.round((stats.underMonitoring / totalOrgs) * 100) : 0;

  // Width helper for the stacked progress bars below — guards against
  // division by zero when a count is 0
  const splitWidth = (part, total) => (total > 0 ? (part / total) * 100 : 0);

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Build avatar src for mobile header
  const avatarSrc =
    avatarPath && userId
      ? `${getAvatarUrl(userId, avatarPath)}?v=${avatarVersion}`
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(adminName)}&background=0D3B2E&color=fff`;

  const displayedRecentSubmissions = isOffCampus
    ? recentOffcampusSubmissions
    : recentSubmissions;

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-[32px] font-bold text-gray-900 tracking-tight">
            {greeting},{" "}
            <span className="text-primary">{adminName || "Admin"}!</span>
          </h1>
          <p className="text-gray-500 mt-1 text-[15px] font-medium">
            {isOffCampus
              ? "Here's what's happening with off-campus activities today."
              : "Here's what's happening with student organizations today."}
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/announce")}
          className="bg-sidebar hover:bg-primary text-white px-5 py-2.5 rounded-lg flex items-center space-x-2 transition-colors font-semibold shadow-sm"
        >
          <Icons.Megaphone />
          <span>Announce</span>
        </button>
      </div>

      {/* Lock Date Card — Hidden for Off-Campus Admin */}
      {!isOffCampus && (
        academicYear ? (
          <div className="bg-sidebar rounded-[10px] p-5 text-white shadow-lg relative overflow-hidden flex flex-col md:flex-row md:items-center w-fit mb-6 pr-12">
            <div className="z-10 mr-12 mb-4 md:mb-0 ml-5">
              <p className="text-[10px] text-gray-300 tracking-widest uppercase font-semibold mb-1">
                Current Cycle Lock Date — {academicYear.year_label}
              </p>
              <h3 className="text-3xl font-bold mb-3 tracking-tight">
                {new Date(academicYear.lock_date).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h3>
              <div
                className={`inline-flex items-center space-x-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                  new Date(academicYear.lock_date) > new Date()
                    ? "bg-green-500/20 text-green-400 border-green-500/30"
                    : "bg-red-500/20 text-red-400 border-red-500/30"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    new Date(academicYear.lock_date) > new Date()
                      ? "bg-green-400"
                      : "bg-red-400"
                  }`}
                ></span>
                <span className="font-semibold">
                  {new Date(academicYear.lock_date) > new Date()
                    ? "Submissions Open"
                    : "Submissions Closed"}
                </span>
              </div>
            </div>

            <div className="flex space-x-3 z-10">
              {timeLeft.map((val, idx) => {
                const labels = ["DAYS", "HOURS", "MINS", "SECS"];
                return (
                  <div key={idx} className="flex flex-col items-center">
                    <div className="bg-[#00A358] rounded-lg w-14 h-16 flex items-center justify-center shadow-inner">
                      <span className="text-2xl font-bold">{val}</span>
                    </div>
                    <span className="text-[9px] text-gray-400 mt-1.5 font-bold tracking-wider">
                      {labels[idx]}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Decorative elements */}
            <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
            <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-primary/40 rounded-full blur-xl"></div>
          </div>
        ) : (
          <div className="bg-gray-100 border border-gray-200 rounded-[10px] p-5 text-gray-500 text-sm mb-6 w-fit">
            No academic year cycle has been set yet. Set one in Settings to show
            the submission deadline countdown.
          </div>
        )
      )}

      {/* Summary Stats Container — shows real counts */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Card 1: Total Organizations -> Total Users for Off-Campus */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                {isOffCampus ? (
                  "Total Users"
                ) : (
                  <>
                    Total
                    <br />
                    Organizations
                  </>
                )}
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-2">
                {isOffCampus ? stats.totalOffcampusUsers : stats.totalActive}
              </h3>
            </div>
          </div>
        </div>

        {/* Card 2: Pending */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                Pending
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-1">
                {isOffCampus ? stats.pending : stats.pending}
              </h3>
            </div>
          </div>
        </div>

        {/* Card 3: Accredited/Re-accredited -> Approved for Off-Campus */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                {isOffCampus ? (
                  "Approved"
                ) : (
                  <>
                    Accredited/
                    <br />
                    Re-accredited
                  </>
                )}
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-1">
                {isOffCampus ? stats.approvedOffcampus : stats.approved}
              </h3>
            </div>
          </div>
        </div>

        {/* Card 4: Under Monitoring -> Rejected for Off-Campus */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                {isOffCampus ? (
                  "Rejected"
                ) : (
                  <>
                    Under
                    <br />
                    Monitoring
                  </>
                )}
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-2">
                {isOffCampus ? stats.rejectedOffcampus : stats.underMonitoring}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row — Removed for Off-Campus Admin */}
      {!isOffCampus && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="col-span-1 md:col-span-2 bg-white rounded-[20px] p-6 border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[15px] font-bold text-gray-800">
                Accreditation Trends
              </h3>
              <div className="flex space-x-4 text-xs font-semibold">
                <div className="flex items-center space-x-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400"></span>
                  <span className="text-gray-500">Pending</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                  <span className="text-gray-500">Accredited/Re-accredited</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                  <span className="text-gray-500">For Interview</span>
                </div>
              </div>
            </div>
            <div className="h-[260px] w-full flex flex-col">
              <TrendsChart trendData={trendData} />
            </div>
          </div>
          <div className="col-span-1 bg-white rounded-[20px] p-6 border border-gray-200 shadow-sm">
            <h3 className="text-[15px] font-bold text-gray-800 mb-6">
              Status Distribution
            </h3>
            <div className="flex items-center justify-between h-[260px]">
              <div className="w-40 h-40 flex-shrink-0">
                <DistributionChart
                  pending={stats.pending}
                  accredited={stats.approved}
                  forInterview={stats.underMonitoring}
                />
              </div>
              <div className="flex flex-col justify-center space-y-4 pr-2">
                <div className="text-xs">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400"></span>
                    <span className="font-semibold text-gray-600">Pending</span>
                  </div>
                  <span className="text-gray-500 ml-4.5 block font-medium">
                    {pendingPct}%
                  </span>
                </div>
                <div className="text-xs">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                    <span className="font-semibold text-gray-600">
                      Accredited/Re-accredited
                    </span>
                  </div>
                  <span className="text-gray-500 ml-4.5 block font-medium">
                    {approvedPct}%
                  </span>
                </div>
                <div className="text-xs">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                    <span className="font-semibold text-gray-600">
                      For Interview
                    </span>
                  </div>
                  <span className="text-gray-500 ml-4.5 block font-medium">
                    {forInterviewPct}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table — shows real recent submissions (Accreditation or Off-Campus) */}
      <div className="bg-white rounded-[20px] border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-[#086C53] px-7 py-4 flex justify-between items-center">
          <h3 className="text-white font-bold text-lg">
            {isOffCampus
              ? "Recent Off-Campus Activity Submissions"
              : "Recent Accreditation Submissions"}
          </h3>
          <button
            onClick={() =>
              navigate(
                isOffCampus ? "/admin/off-campus" : "/admin/accreditation",
              )
            }
            className="text-white hover:text-gray-200 text-sm font-semibold flex items-center transition-colors"
          >
            View All <Icons.ChevronRight />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed min-w-[700px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-7 py-4 text-xs font-bold text-primary uppercase tracking-wider w-[30%] min-w-[180px]">
                  {isOffCampus ? 'Activity' : 'Organization'}
                </th>
                <th className="px-7 py-4 text-xs font-bold text-primary uppercase tracking-wider w-[24%] min-w-[160px]">
                  Representative
                </th>
                <th className="px-7 py-4 text-xs font-bold text-primary uppercase tracking-wider w-[16%] min-w-[120px]">
                  College/Campus
                </th>
                <th className="px-7 py-4 text-xs font-bold text-primary uppercase tracking-wider w-[18%] min-w-[140px]">
                  Completion
                </th>
                <th className="px-7 py-4 text-xs font-bold text-primary uppercase tracking-wider w-[12%] min-w-[100px]">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayedRecentSubmissions.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-7 py-10 text-center text-gray-400 text-sm"
                  >
                    No recent submissions yet.
                  </td>
                </tr>
              ) : (
                displayedRecentSubmissions.map((sub, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate(
                        isOffCampus
                           ? "/admin/off-campus"
                          : "/admin/accreditation",
                        { state: { openOrgId: sub.organization_id } },
                      )
                    }
                  >
                    <td className="px-7 py-4">
                      <div
                        className="text-sm font-semibold text-gray-700 break-words line-clamp-2 leading-snug"
                        title={sub.org}
                      >
                        {sub.org}
                      </div>
                    </td>
                    <td className="px-7 py-4">
                      <div
                        className="text-sm font-semibold text-gray-700 truncate"
                        title={sub.repName}
                      >
                        {sub.repName}
                      </div>
                      <div
                        className="text-[11px] text-gray-400 font-medium truncate"
                        title={sub.repEmail}
                      >
                        {sub.repEmail}
                      </div>
                    </td>
                    <td className="px-7 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                      {sub.college}
                    </td>
                    <td className="px-7 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="text-[11px] font-bold text-gray-600 w-10">
                          {sub.comp}%
                        </div>
                        <div className="w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden flex">
                          <div
                            className="bg-sidebar h-1.5"
                            style={{ width: `${sub.comp}%` }}
                          ></div>
                        </div>
                        {sub.missing > 0 && (
                          <span className="bg-red-100 text-red-600 text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0">
                            {sub.missing} missing
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-7 py-4 whitespace-nowrap">
                      <span
                        className={`text-xs font-bold px-3 py-1 rounded-full ${
                          sub.status === "Approved" || sub.status === "Reviewed"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {sub.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Document Analytics — Removed for Off-Campus Admin */}
      {!isOffCampus && <DocumentAnalytics />}

      {/* Quick Access — Removed for Off-Campus Admin */}
      {!isOffCampus && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              onClick={() => navigate("/admin/settings/requirements")}
              className="bg-white p-5 rounded-[16px] border border-gray-200 shadow-sm cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2 text-gray-800">
                  <Icons.Form1 className="w-5 h-5 text-gray-700 group-hover:text-primary transition-colors" />
                  <h4 className="font-bold text-sm">Accreditation Forms</h4>
                </div>
                <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded">
                  15
                </span>
              </div>
              <p className="text-xs text-gray-500 font-medium">
                View accreditation forms
              </p>
            </div>
            <div
              onClick={() => navigate("/admin/settings/academic-year")}
              className="bg-white p-5 rounded-[16px] border border-gray-200 shadow-sm cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2 text-gray-800">
                  <svg
                    className="w-5 h-5 text-gray-700 group-hover:text-primary transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.75}
                      d="M12 14l9-5-9-5-9 5 9 5z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.75}
                      d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.75}
                      d="M12 14v7"
                    />
                  </svg>
                  <h4 className="font-bold text-sm">Academic Year</h4>
                </div>
                <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded">
                  {academicYear?.year || "Active"}
                </span>
              </div>
              <p className="text-xs text-gray-500 font-medium">
                {academicYear?.year
                  ? `A.Y. ${academicYear.year} Cycle`
                  : "Manage academic year cycle"}
              </p>
            </div>
            <div
              onClick={() => navigate("/admin/settings/deadline")}
              className="bg-white p-5 rounded-[16px] border border-gray-200 shadow-sm cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2 text-gray-800">
                  <Icons.Calendar />
                  <h4 className="font-bold text-sm">Deadline Settings</h4>
                </div>
                <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded">
                  {academicYear ? `${timeLeft[0]}d` : "Set"}
                </span>
              </div>
              <p className="text-xs text-gray-500 font-medium">
                {academicYear
                  ? `${timeLeft[0]} days remaining`
                  : "No deadline set yet"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;